import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, AlertTriangle, Upload } from "lucide-react";

export default function ImportPreviewDialog({ open, onOpenChange, title, columns, preview, onConfirm, importing }) {
  if (!preview) return null;
  const { rows = [], total = 0, validi = 0, scartati = 0 } = preview;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] overflow-hidden" data-testid="import-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription>Controlla le righe rilevate. Solo quelle valide verranno importate.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 text-xs font-semibold">
          <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600" data-testid="preview-total">Totale: {total}</span>
          <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700" data-testid="preview-validi">Valide: {validi}</span>
          <span className="px-2.5 py-1 rounded-full bg-rose-50 text-rose-700" data-testid="preview-scartati">Scartate: {scartati}</span>
        </div>

        <div className="overflow-auto max-h-[50vh] border border-slate-200 rounded-lg min-w-0">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr className="text-left">
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-widest text-slate-500 w-24">Esito</th>
                <th className="px-3 py-2 text-xs font-semibold uppercase tracking-widest text-slate-500 w-12">#</th>
                {columns.map((c) => (
                  <th key={c.key} className={`px-3 py-2 text-xs font-semibold uppercase tracking-widest text-slate-500 ${c.right ? "text-right" : ""}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.riga ?? i} data-testid={`preview-row-${i}`} className={`border-b border-slate-100 ${r.valid ? "" : "bg-rose-50/40"}`}>
                  <td className="px-3 py-2">
                    {r.valid ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium"><Check className="h-3.5 w-3.5" /> OK</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-rose-600 text-xs font-medium" title={r.motivo}><AlertTriangle className="h-3.5 w-3.5" /> Scarta</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-400 tabular-nums">{r.riga}</td>
                  {columns.map((c) => (
                    <td key={c.key} className={`px-3 py-2 ${c.right ? "text-right tabular-nums" : "text-slate-700"} ${!r.valid ? "text-slate-400" : ""}`}>
                      {c.format ? c.format(r[c.key]) : (r[c.key] ?? "")}
                      {c.key === columns[0].key && r.tratta_nuova && <span className="ml-1.5 text-[10px] text-blue-600 font-semibold">(nuova)</span>}
                    </td>
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={columns.length + 2} className="px-3 py-8 text-center text-slate-400">Nessuna riga rilevata nel file.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {scartati > 0 && (
          <p className="text-xs text-rose-600">{scartati} righe verranno ignorate. Passa il mouse sull'icona "Scarta" per il motivo.</p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="preview-cancel-button">Annulla</Button>
          <Button data-testid="preview-confirm-button" disabled={validi === 0 || importing} onClick={onConfirm} className="bg-blue-700 hover:bg-blue-800">
            <Upload className="h-4 w-4 mr-1.5" /> {importing ? "Importazione…" : `Importa ${validi} righe`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
